import * as signalR from "@microsoft/signalr";
import { getAuthProps } from "../authentication/authLibrary";

// ─── Config ───────────────────────────────────────────────────────────────────

const environment = process.env.REACT_APP_ENVIRONMENT;

const API_CONFIG = {
  production:    process.env.REACT_APP_LIVEURL,
  preproduction: process.env.REACT_APP_TESTURL,
  default:       process.env.REACT_APP_LOCALURL,
};

const BASE_URL = API_CONFIG[environment] || API_CONFIG.default || "";
const HUB_URL = `${BASE_URL}/hubs/notifications`;

// ─── Singleton Connection ────────────────────────────────────────────────────

let _connection = null;

/**
 * Builds (or returns the existing) SignalR HubConnection.
 * The JWT is passed as a query-string parameter because browsers cannot
 * set custom headers during WebSocket upgrade handshakes.
 */
const buildConnection = () => {
  if (_connection) return _connection;

  _connection = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: () => {
        const authData = getAuthProps();
        return authData?.token?.token ?? null;
      },
      // Prefer WebSockets, fall back to Server-Sent Events then Long Polling
      transport:
        signalR.HttpTransportType.WebSockets |
        signalR.HttpTransportType.ServerSentEvents |
        signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // retry delays in ms
    .configureLogging(
      process.env.NODE_ENV === "development"
        ? signalR.LogLevel.Information
        : signalR.LogLevel.Error,
    )
    .build();

  return _connection;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns the singleton HubConnection, creating it on first call.
 */
export const getHubConnection = () => buildConnection();

/**
 * Starts the connection if it is not already connected/connecting.
 * @returns {Promise<void>}
 */
export const startConnection = async () => {
  const conn = getHubConnection();

  if (
    conn.state === signalR.HubConnectionState.Connected ||
    conn.state === signalR.HubConnectionState.Connecting ||
    conn.state === signalR.HubConnectionState.Reconnecting
  ) {
    return;
  }

  try {
    await conn.start();
    console.info("[SignalR] Connected to notification hub.");
  } catch (err) {
    console.error("[SignalR] Connection failed:", err);
  }
};

/**
 * Stops the connection gracefully.
 * @returns {Promise<void>}
 */
export const stopConnection = async () => {
  if (!_connection) return;

  try {
    await _connection.stop();
    console.info("[SignalR] Disconnected from notification hub.");
  } catch (err) {
    console.error("[SignalR] Error stopping connection:", err);
  } finally {
    _connection = null;
  }
};
